import { type FC, type ReactNode, useCallback, useMemo } from 'react';

import { breakpoints } from '@vakantiesnl/components/src/styles/breakpoints';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent from '@vakantiesnl/image-component';
import { type MicroCopy } from '@vakantiesnl/types/src';
import { type MappedBucketImgProps } from '@vakantiesnl/types/src/nbc';

import { useCustomStyles } from './ImageOverview.style';
import { Tag } from '../../atoms/Tag';
import { ImagePlaceholder } from '../ImagePlaceholder';
import { RatingBox } from '../RatingBox';

const DESKTOP_MAX_GALLERY_HEIGHT = 382;
const MOBILE_MAX_GALLERY_HEIGHT = 211;

const SMALL_DETAIL_IMAGE_THUMBNAIL: [number, number] = [200, DESKTOP_MAX_GALLERY_HEIGHT / 2];
const MEDIUM_DETAIL_IMAGE_THUMBNAIL: [number, number] = [400, DESKTOP_MAX_GALLERY_HEIGHT / 2];
const LARGE_DETAIL_IMAGE_THUMBNAIL: [number, number] = [400, DESKTOP_MAX_GALLERY_HEIGHT];

/**
 * Trim sizes to support cropping of images
 * First object is for the total amount of images
 * Next Object is for the position of the image
 */
const TRIM_SIZES: Record<string, Record<string, Record<string, [number, number]>>> = {
	1: {
		0: {
			lg: [1200, DESKTOP_MAX_GALLERY_HEIGHT],
			sm: [768, MOBILE_MAX_GALLERY_HEIGHT],
		},
	},
	2: {
		0: {
			lg: [800, DESKTOP_MAX_GALLERY_HEIGHT],
			sm: [768, MOBILE_MAX_GALLERY_HEIGHT],
		},
		1: {
			lg: LARGE_DETAIL_IMAGE_THUMBNAIL,
		},
	},
	3: {
		0: {
			lg: [800, DESKTOP_MAX_GALLERY_HEIGHT],
			sm: [768, MOBILE_MAX_GALLERY_HEIGHT],
		},
		1: {
			lg: MEDIUM_DETAIL_IMAGE_THUMBNAIL,
		},
		2: {
			lg: MEDIUM_DETAIL_IMAGE_THUMBNAIL,
		},
	},
	4: {
		0: {
			lg: [800, DESKTOP_MAX_GALLERY_HEIGHT],
			sm: [768, MOBILE_MAX_GALLERY_HEIGHT],
		},
		1: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
		2: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
		3: {
			lg: MEDIUM_DETAIL_IMAGE_THUMBNAIL,
		},
	},
	5: {
		0: {
			lg: [800, DESKTOP_MAX_GALLERY_HEIGHT],
			sm: [768, MOBILE_MAX_GALLERY_HEIGHT],
		},
		1: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
		2: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
		3: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
		4: {
			lg: SMALL_DETAIL_IMAGE_THUMBNAIL,
		},
	},
};

export interface ImageOverviewProps {
	/** List of images to render */
	images: MappedBucketImgProps[];
	/** Amount of images to show */
	imagesToShow?: number;
	/** Rating or reviews */
	rating?: number;
	/** Click handler for images */
	onImageClick?: (index: number) => void;
	/** Mapped link of the review counter */
	reviewCountLink?: ReactNode;
	/**
	 * Count of remaining images
	 * Will be displayed on the last detail image when amount of images is bigger then the amount of @imagesToShow
	 **/
	imagesCount: number;
	/** Microcopies */
	microCopies: MicroCopy;
	/** Component shown over the acco image */
	imageOverlayComponent?: ReactNode;
}

export const ImageOverview: FC<ImageOverviewProps> = ({
	images,
	imagesToShow = 5,
	rating,
	onImageClick,
	reviewCountLink,
	microCopies,
	imagesCount,
	imageOverlayComponent,
}) => {
	/** Slice the images to the total amount of images to show to support using the indexes and length */
	const mappedImages = useMemo(() => images.slice(0, imagesToShow), [images, imagesToShow]);
	const detailImages = useMemo(() => mappedImages.slice(1, imagesToShow), [mappedImages, imagesToShow]);
	const hasShowMoreImages = useMemo(() => imagesCount > mappedImages.length, [imagesCount, mappedImages]);
	const extraImagesCount = useMemo(() => imagesCount - mappedImages.length, [imagesCount, mappedImages]);

	const { classes, cx } = useCustomStyles({ imagesCount: detailImages.length });
	const { isMobile } = useBreakpoints();

	/** Click handler dispatched on image click */
	const handleImageClick = useCallback(
		(i: number) => () => {
			onImageClick && onImageClick(i);
		},
		[onImageClick],
	);

	return (
		<div className={classes.container} data-cy="image-gallery">
			<div className={cx(classes.mainImage)} onClick={handleImageClick(0)}>
				{!!rating && reviewCountLink && (
					<div className={classes.ratingContainer}>
						<RatingBox rating={rating} microCopies={microCopies} label={true}>
							<span>{reviewCountLink}</span>
						</RatingBox>
					</div>
				)}
				{imageOverlayComponent && isMobile && (
					<div className={classes.overlayComponentContainer}>{imageOverlayComponent}</div>
				)}

				{mappedImages.length === 0 ? (
					<ImagePlaceholder placeholder={microCopies['gallery.no-images.text']} />
				) : (
					<>
						<ImageComponent
							className={cx(classes.image, {
								[classes.clickable]: !!onImageClick,
							})}
							src={mappedImages[0].url}
							rotationDegrees={mappedImages[0].rotationDegrees}
							fill={true}
							priority={true}
							alt=""
							sizes={`
						(max-width: ${breakpoints.md}px) ${TRIM_SIZES[mappedImages.length][0].sm[0]}px,
              			${TRIM_SIZES[mappedImages.length][0].lg[0]}px
						`}
							trim={isMobile ? TRIM_SIZES[imagesToShow][0].sm : TRIM_SIZES[imagesToShow][0].lg}
						/>
						{((isMobile && hasShowMoreImages) || (hasShowMoreImages && imagesToShow === 1)) && (
							<div className={classes.tagContainer}>
								<Tag size="medium" color="black" leadingIcon="image">
									{imagesCount}
								</Tag>
							</div>
						)}
					</>
				)}
			</div>
			{!isMobile && detailImages.length > 0 && (
				<div className={classes.detailImages}>
					{detailImages.map((image, i) => {
						// Add 1 to the index to keep the index of images persisted
						const remappedIndex = i + 1;

						return (
							<div
								className={cx(classes.image, classes.detailImage, classes.detailImage + remappedIndex, {
									[classes.clickable]: !!onImageClick,
								})}
								key={i}
								onClick={handleImageClick(remappedIndex)}
							>
								{hasShowMoreImages && remappedIndex === detailImages.length && (
									<div className={classes.overlay}>
										<span className={classes.imagesCount}>{extraImagesCount}</span>
									</div>
								)}
								<ImageComponent
									src={image.url}
									className={cx(classes.image, {
										[classes.clickable]: !!onImageClick,
									})}
									rotationDegrees={image.rotationDegrees}
									fill={true}
									alt=""
									sizes={`${TRIM_SIZES[mappedImages.length][remappedIndex].lg[0]}px`}
									trim={TRIM_SIZES[mappedImages.length][remappedIndex].lg}
								/>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
